import { ValuationInput, ValuationCalculation, MediaEntryInput } from '../types';

/**
 * TV Size Category Multipliers
 * Exported for the multi-entry per-line calculation and TV sync-back
 * (see docs/MEDIA_VALUE_MULTI_ENTRY_PLAN.md).
 */
export const TV_SIZE_MULTIPLIERS = {
  S: 0.2, // Small (≤1% of screen area)
  M: 0.7, // Medium (~1-5% of screen area)
  L: 1.0, // Large (~5-15% of screen area)
  XL: 1.5, // Extra Large (>15% of screen area)
};

/**
 * Calculate Sales Value
 * Formula: Merchandise Revenue + Matchday Revenue + Player Sales Value
 */
export function calculateSalesValue(data: ValuationInput): number {
  return (data.merch_revenue || 0) + (data.matchday_revenue || 0) + (data.player_sales_value || 0);
}

/**
 * Calculate TV Media Value
 * Formula: Duration × Size Multiplier × CPM × Viewers ÷ 1,000
 */
export function calculateTvValue(data: ValuationInput): number {
  const sizeMultiplier = TV_SIZE_MULTIPLIERS[data.tv_size_category] || TV_SIZE_MULTIPLIERS.M;
  return (
    ((data.tv_duration_min || 0) * sizeMultiplier * (data.tv_cpm || 0) * (data.tv_viewers || 0)) /
    1000
  );
}

/**
 * Calculate Web TV Value
 * Formula: Views × CPM ÷ 1,000
 */
export function calculateWebTvValue(data: ValuationInput): number {
  return ((data.webtv_views || 0) * (data.webtv_cpm || 0)) / 1000;
}

/**
 * Calculate PR Value
 * Formula: Reach × CPM ÷ 1,000
 */
export function calculatePrValue(data: ValuationInput): number {
  return ((data.pr_reach || 0) * (data.pr_cpm || 0)) / 1000;
}

/**
 * Calculate Digital Value
 * Formula: Impressions × CPM ÷ 1,000
 */
export function calculateDigitalValue(data: ValuationInput): number {
  return ((data.digital_impressions || 0) * (data.digital_cpm || 0)) / 1000;
}

/**
 * Calculate Social Media Value
 * Formula: (Followers × CPM + Impressions × CPM + Engagements × CPM) ÷ 1,000
 */
export function calculateSocialValue(data: ValuationInput): {
  followers: number;
  impressions: number;
  engagements: number;
  total: number;
} {
  const followers = ((data.social_followers || 0) * (data.social_followers_cpm || 0)) / 1000;
  const impressions = ((data.social_impressions || 0) * (data.social_impressions_cpm || 0)) / 1000;
  const engagements = ((data.social_engagements || 0) * (data.social_cpm || 0)) / 1000;

  return {
    followers,
    impressions,
    engagements,
    total: followers + impressions + engagements,
  };
}

/**
 * Calculate Influencer Value (summary columns)
 * Formula: (Reach × CPM + Impressions × CPM + Engagements × CPM) ÷ 1,000
 * Same shape as Social Media (three metric pairs).
 */
export function calculateInfluencerValue(data: ValuationInput): {
  reach: number;
  impressions: number;
  engagements: number;
  total: number;
} {
  const reach = ((data.influencer_reach || 0) * (data.influencer_reach_cpm || 0)) / 1000;
  const impressions =
    ((data.influencer_impressions || 0) * (data.influencer_impressions_cpm || 0)) / 1000;
  const engagements =
    ((data.influencer_engagements || 0) * (data.influencer_engagements_cpm || 0)) / 1000;

  return {
    reach,
    impressions,
    engagements,
    total: reach + impressions + engagements,
  };
}

/**
 * Calculate the value of ONE multi-entry line (valuation_media_entries row).
 * Shared by API aggregation (summary sync-back) and UI live totals so both
 * always agree. `volume` meaning depends on channel — see MediaEntryInput.
 */
export function calculateMediaEntryValue(entry: MediaEntryInput): number {
  switch (entry.channel) {
    case 'tv': {
      const sizeMultiplier =
        TV_SIZE_MULTIPLIERS[entry.size_category || 'M'] || TV_SIZE_MULTIPLIERS.M;
      return (
        ((entry.duration_min || 0) * sizeMultiplier * (entry.cpm || 0) * (entry.volume || 0)) / 1000
      );
    }
    case 'webtv':
    case 'pr':
    case 'digital':
      return ((entry.volume || 0) * (entry.cpm || 0)) / 1000;
    case 'influencer':
      return (
        ((entry.volume || 0) * (entry.cpm || 0) +
          (entry.impressions || 0) * (entry.impressions_cpm || 0) +
          (entry.engagements || 0) * (entry.engagements_cpm || 0)) /
        1000
      );
    case 'merchandise':
    case 'matchday':
      // Amount lines — the line value IS the entered amount
      return entry.volume || 0;
  }
}

/**
 * Compute the summary-column values for the channels present in `entries`
 * (plan 5.3). Linear channels get exact totals + weighted-average CPM;
 * TV gets a back-solved effective CPM (D3) so the legacy formula reproduces
 * the sum of line values exactly.
 *
 * Client-safe — shared by the media-entries service (server sync-back) and
 * ValuationForm (live calculator) so both always agree.
 */
export function buildSummaryFromEntries(
  entries: MediaEntryInput[]
): Record<string, number | string> {
  const num = (value: unknown): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const summary: Record<string, number | string> = {};

  const byChannel = new Map<MediaEntryInput['channel'], MediaEntryInput[]>();
  for (const entry of entries) {
    const list = byChannel.get(entry.channel) || [];
    list.push(entry);
    byChannel.set(entry.channel, list);
  }

  for (const [channel, lines] of byChannel) {
    const totalVolume = lines.reduce((sum, l) => sum + num(l.volume), 0);
    const totalValue = lines.reduce((sum, l) => sum + calculateMediaEntryValue(l), 0);

    switch (channel) {
      case 'webtv':
        summary.webtv_views = totalVolume;
        summary.webtv_cpm = totalVolume > 0 ? (totalValue * 1000) / totalVolume : 0;
        break;
      case 'pr':
        summary.pr_reach = totalVolume;
        summary.pr_cpm = totalVolume > 0 ? (totalValue * 1000) / totalVolume : 0;
        break;
      case 'digital':
        summary.digital_impressions = totalVolume;
        summary.digital_cpm = totalVolume > 0 ? (totalValue * 1000) / totalVolume : 0;
        break;
      case 'influencer': {
        // Per metric: total + weighted-average CPM (volume/cpm hold reach)
        const impressions = lines.reduce((sum, l) => sum + num(l.impressions), 0);
        const engagements = lines.reduce((sum, l) => sum + num(l.engagements), 0);
        const reachValue = lines.reduce((sum, l) => sum + num(l.volume) * num(l.cpm), 0);
        const imprValue = lines.reduce(
          (sum, l) => sum + num(l.impressions) * num(l.impressions_cpm),
          0
        );
        const engValue = lines.reduce(
          (sum, l) => sum + num(l.engagements) * num(l.engagements_cpm),
          0
        );
        summary.influencer_reach = totalVolume;
        summary.influencer_reach_cpm = totalVolume > 0 ? reachValue / totalVolume : 0;
        summary.influencer_impressions = impressions;
        summary.influencer_impressions_cpm = impressions > 0 ? imprValue / impressions : 0;
        summary.influencer_engagements = engagements;
        summary.influencer_engagements_cpm = engagements > 0 ? engValue / engagements : 0;
        break;
      }
      case 'merchandise':
        summary.merch_revenue = totalVolume;
        break;
      case 'matchday':
        summary.matchday_revenue = totalVolume;
        break;
      case 'tv': {
        // Back-solved effective CPM (D3): neutral size L (×1.0),
        // volume-weighted average duration, cpm so that the legacy formula
        // (duration × 1.0 × cpm × viewers / 1000) equals Σ line values.
        const weightedDuration =
          totalVolume > 0
            ? lines.reduce((sum, l) => sum + num(l.duration_min) * num(l.volume), 0) / totalVolume
            : lines.reduce((sum, l) => sum + num(l.duration_min), 0) / (lines.length || 1);
        // tv_duration_min is an integer column/schema field — round it (min 1
        // when non-zero) and back-solve the cpm from the ROUNDED duration so
        // the legacy formula still equals Σ line values exactly.
        const roundedDuration =
          weightedDuration > 0 ? Math.max(1, Math.round(weightedDuration)) : 0;
        summary.tv_viewers = totalVolume;
        summary.tv_size_category = 'L'; // neutral multiplier 1.0 — required by the back-solve
        summary.tv_duration_min = roundedDuration;
        summary.tv_cpm =
          roundedDuration > 0 && totalVolume > 0
            ? (totalValue * 1000) / (roundedDuration * totalVolume)
            : 0;
        break;
      }
    }
  }

  return summary;
}

/**
 * Calculate Match Attendance Value
 * Formula: Attendees × CPM ÷ 1,000
 */
export function calculateAttendanceValue(data: ValuationInput): number {
  return ((data.attendance || 0) * (data.attendance_cpm || 0)) / 1000;
}

/**
 * Calculate Hospitality Value
 * Formula: Guests × Value per Guest
 */
export function calculateHospitalityValue(data: ValuationInput): number {
  return (data.hospitality_guests || 0) * (data.hospitality_value_per_guest || 0);
}

/**
 * Calculate Total Media Value
 * Sum of all media channel values
 */
export function calculateMediaValue(data: ValuationInput): ValuationCalculation['mediaValue'] {
  const tv = calculateTvValue(data);
  const webTv = calculateWebTvValue(data);
  const pr = calculatePrValue(data);
  const digital = calculateDigitalValue(data);
  const influencer = calculateInfluencerValue(data);
  const social = calculateSocialValue(data);
  const attendance = calculateAttendanceValue(data);
  const hospitality = calculateHospitalityValue(data);

  return {
    tv,
    webTv,
    pr,
    digital,
    influencer,
    social,
    attendance,
    hospitality,
    total: tv + webTv + pr + digital + influencer.total + social.total + attendance + hospitality,
  };
}

/**
 * Calculate Qualitative Multiplier
 * Formula: 0.5 + (Average Score - 1) × (1.5 ÷ 9)
 *
 * Scale Conversion:
 * - Score 1 = 0.5x multiplier
 * - Score 4 = 1.0x multiplier (neutral)
 * - Score 10 = 2.0x multiplier
 */
export function calculateQualitativeMultiplier(data: ValuationInput): number {
  const averageScore =
    ((data.brand_popularity || 3) +
      (data.brand_csr || 3) +
      (data.brand_culture || 3) +
      (data.brand_loyalty || 3)) /
    4;

  const multiplier = 0.5 + (averageScore - 1) * (1.5 / 9);

  // Round to 2 decimal places
  return Math.round(multiplier * 100) / 100;
}

/**
 * Calculate Complete Valuation
 * Final Total = (Sales Value + Media Value) × Qualitative Multiplier
 */
export function calculateCompleteValuation(data: ValuationInput): ValuationCalculation {
  const salesValue = calculateSalesValue(data);
  const mediaValue = calculateMediaValue(data);
  const qualitativeMultiplier = calculateQualitativeMultiplier(data);
  const finalTotal = (salesValue + mediaValue.total) * qualitativeMultiplier;

  return {
    salesValue,
    mediaValue,
    qualitativeMultiplier,
    finalTotal: Math.round(finalTotal * 100) / 100, // Round to 2 decimal places
  };
}
